import { Storage } from "./storage";
import { HttpClient } from "@angular/common/http";
import { concatMap, Observable, of, Subject } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { Configuration } from "../../assets/Configuration";
import { CoreScormData } from "../models/scorm/scorm-core.model";
import { ScormInteraction } from "../models/scorm/scorm-interaction.model";

declare const createEasyScorm: any;

export class SCORMAdapter {
    saveUrl: string;
    getAllUrl: string;
    private isFinishedSubject = new Subject<boolean>();
    public isFinished$ = this.isFinishedSubject.asObservable();
    private valuesToSaveSubj = new Subject<LmsValue>();
    private valuesToSave$: Observable<LmsValue> = this.valuesToSaveSubj.asObservable();

    constructor(
        private httpClient: HttpClient,
        private courseTemplateId: string
    ) {
        this.saveUrl = Configuration.getApiBaseUrl() + "/scorm/lms/" + courseTemplateId;
        this.getAllUrl = Configuration.getApiBaseUrl() + "/scorm/lms/" + courseTemplateId;
        this.valuesToSave$.pipe(
            concatMap(valueToSave => {
                return this.httpClient.post<void>(this.saveUrl, valueToSave);
            }), catchError(() => of()),
        ).subscribe({
            complete: () => this.isFinishedSubject.next(true)
        });
    }

    LMSInitialize() {
        console.log('LMS Initialize');
        Storage.setItem('Initialized', true);
        return true;
    }

    loadValuesFromLmsAndInitData(personId: string, personName: string): Observable<any> {
        return this.httpClient.get<{ element: string, value: string }[]>(this.getAllUrl)
            .pipe(tap(lmsValues => {
                Storage.clearAll();
               
                for (let element of lmsValues) {
                    Storage.setItem(element.element, element.value);
                }

                Storage.setItem("cmi.core.lesson_mode", "normal");
                Storage.setItem("cmi.core.student_id", personId);
                Storage.setItem("cmi.core.student_name", personName);
            })
        );
    }


    LMSFinish() {
        if (!this._isInitialized()) {
            this._setError(301);
            return false;
        }
        this.valuesToSaveSubj.complete();
        let _return = this.LMSCommit();
        Storage.setItem('Initialized', false);
        Storage.clearAll();
        return _return;
    }

    LMSGetValue(element: any) {
        if (!this._isInitialized()) {
            this._setError(301);
            return false;
        }
        let value = Storage.getItem(element);
        if (!value) {
            this._setError(201);
            return "";
        }
        return value;
    }

    LMSSetValue(element: string, value: string) {
        if (!this._isInitialized()) {
            this._setError(301);
            return false;
        }

        this.valuesToSaveSubj.next({element: element, value: value});
        Storage.setItem(element, value);
        return Storage.getItem(element);
    }

    LMSCommit() {
        return true;
    }

    LMSGetLastError() {
        let errors = Storage.getItem('errors');
        errors = JSON.parse(errors);
        if (errors && errors.length > 0) {
            return errors.pop();
        }
        return "";
    }

    LMSGetErrorString(errorCode: string) {
        if (errorCode == null) return "";

        const codeStr = errorCode.toString();
        const errorInfo = this.errorDefinition[codeStr];
        return errorInfo?.errorString || "";
    }

    LMSGetDiagnostic(errorCode: any) {
        if (errorCode == null) return "";

        const codeStr = errorCode.toString();
        const errorInfo = this.errorDefinition[codeStr];
        return errorInfo?.errorString || "";
    }

    private _isInitialized() {
        return Storage.getItem('Initialized');
    }

    private _setError(errorCode: any) {
        errorCode = errorCode.toString();
        let errors = Storage.getItem('errors');
        if (!errors) errors = '[]';
        errors = JSON.parse(errors);
        errors.push(errorCode);
        errors = JSON.stringify(errors);
        Storage.setItem('errors', errors);
    }

    private getCoreFromStorage(data: { [key: string]: any }, learner: { id: string; name: string }): CoreScormData {
        const core: CoreScormData = {
            lesson_mode: data["cmi.core.lesson_mode"] || "normal",
            student_id: data["cmi.core.student_id"] || learner.id,
            student_name: data["cmi.core.student_name"] || learner.name,
            lesson_location: data["cmi.core.lesson_location"] || "",
            credit: data["cmi.core.credit"] || "",
            lesson_status: data["cmi.core.lesson_status"] || "not attempted",
            entry: data["cmi.core.entry"] || "",
            score: {
                raw: data["cmi.core.score.raw"] || "",
                min: data["cmi.core.score.min"] || "",
                max: data["cmi.core.score.max"] || ""
            },
            total_time: data["cmi.core.total_time"] || "0000:00:00.00",
            exit: data["cmi.core.exit"] || "",
            session_time: data["cmi.core.session_time"] || ""
        };

        return core;
    }

    private getInteractionsFromStorage(data: { [key: string]: any }): ScormInteraction[] {
        const interactions: ScormInteraction[] = [];
        const indexes = new Set<number>();

        for (const key of Object.keys(data)) {
            const match = key.match(/^cmi\.interactions\.(\d+)\.(.+)$/);
            if (!match) continue;
            indexes.add(parseInt(match[1], 10));
        }

        for (const i of Array.from(indexes)) {
            const objectiveId = data[`cmi.interactions.${i}.objectives.0.id`] || "";
            const correctPattern = data[`cmi.interactions.${i}.correct_responses.0.pattern`] || "";

            const objectives = [{ id: objectiveId }];
            const correct_responses = [{ pattern: correctPattern }];

            interactions.push({
                id: data[`cmi.interactions.${i}.id`] || "",
                objectives: objectives,
                correct_responses: correct_responses,
                time: data[`cmi.interactions.${i}.time`] || "",
                type: data[`cmi.interactions.${i}.type`] || "",
                weighting: data[`cmi.interactions.${i}.weighting`] || "",
                student_response: data[`cmi.interactions.${i}.student_response`] || "",
                result: data[`cmi.interactions.${i}.result`] || "",
                latency: data[`cmi.interactions.${i}.latency`] || ""
            });
        }

        if (interactions.length === 0) {
            interactions.push({
                id: "",
                objectives: [{ id: "" }],
                correct_responses: [{ pattern: "" }],
                time: "",
                type: "",
                weighting: "",
                student_response: "",
                result: "",
                latency: ""
            });
        }

        return interactions;
    }

    runScormPlayer(personId: string, personName: string) {
        const id = this.courseTemplateId;
        const learner = { id: personId, name: personName };
        const moduleURL = `/api/scormcontent/${id}`;
        const autoCommitDuration = 5;

        this.loadValuesFromLmsAndInitData(learner.id, learner.name).subscribe(() => {
            createEasyScorm().load(moduleURL).then((scormPlayer: any) => {
                const scoList = scormPlayer.getSCOList();

                const runSCO = (scoObj: any) => {
                    scormPlayer.run("scormplayer_iframe", scoObj.resource.url, learner, {
                        ESInitialize: () => this.LMSInitialize(),
                        ESSetValue: (cmi: string, value: string) => this.LMSSetValue(cmi, value),
                        ESGetValue: (cmi: string) => this.LMSGetValue(cmi),
                        ESCommit: () => this.LMSCommit(),
                        ESTerminate: () => this.LMSFinish(),
                        ESGetLastError: () => this.LMSGetLastError(),
                        ESGetErrorString: (code: string) => this.LMSGetErrorString(code),
                        ESGetDiagnostic: (code: string) => this.LMSGetDiagnostic(code),
                        ESGetUserData: () => {
                            return new Promise((resolve) => {
                                const data = Storage.getAll() || {};

                                const core: CoreScormData = this.getCoreFromStorage(data, learner);
                                data.core = core;

                                const interactions: ScormInteraction[] = this.getInteractionsFromStorage(data);
                                data.interactions = interactions;

                                for (const key of Object.keys(data)) {
                                    if (key.startsWith("cmi.core.") || key.startsWith("cmi.interactions.")) {
                                        delete data[key];
                                    } else if (key.startsWith("cmi.")) {
                                        const newKey = key.replace(/^cmi\./, "");
                                        data[newKey] = data[key];
                                        delete data[key];
                                    }
                                }

                                resolve(data);
                            });
                        },
                        ESSaveUserData: () => {}
                    }, () => { console.log("iFrame Loaded.") }, autoCommitDuration);
                };

                runSCO(scoList.items[0]);
            }).catch((err: any) => {
                alert('SCORM load failed: ' + err.message);
            });
        });
    }

    errorDefinition: { 
        [key: string]: { errorString: string; diagnostic: string } 
    } = {
        "0": {
            errorString: "No Error",
            diagnostic: "No error occurred, the previous API call was successful."
        },
        "101": {
            errorString: "General Exception",
            diagnostic: "No specific error code exists to describe the error."
        },
        "201": {
            errorString: "Invalid argument error",
            diagnostic: "An argument represents an invalid data model element or is otherwise incorrect."
        },
        "202": {
            errorString: "Element cannot have children",
            diagnostic: "LMSGetValue was called with a data model element name that ends in “_children” for a data model element that does not support the “_children” suffix."
        },
        "203": {
            errorString: "Element not an array. Cannot have count",
            diagnostic: "LMSGetValue was called with a data model element name that ends in “_count” for a data model element that does not support the “_count” suffix."
        },
        "301": {
            errorString: "Not initialized",
            diagnostic: "An API call was made before the call to LMSInitialize."
        },
        "401": {
            errorString: "Not implemented error",
            diagnostic: "The data model element indicated in a call to LMSGetValue or LMSSetValue is valid, but was not implemented by this LMS. SCORM 1.2 defines a set of data model elements as being optional for an LMS to implement."
        },
        "402": {
            errorString: "Invalid set value, element is a keyword",
            diagnostic: "LMSSetValue was called on a data model element that represents a keyword (elements that end in \"_children\" and \"_count\")."
        },
        "403": {
            errorString: "Element is read only",
            diagnostic: "LMSSetValue was called with a data model element that can only be read."
        },
        "404": {
            errorString: "Element is write only",
            diagnostic: "LMSGetValue was called on a data model element that can only be written to."
        },
        "405": {
            errorString: "Incorrect Data Type",
            diagnostic: "LMSSetValue was called with a value that is not consistent with the data format of the supplied data model element."
        },
    }
}

export interface LmsValue {
    element: string;
    value: string;
}